/**
 * CauterizeSkillEffect — Fire tree Backdraft Fork 2-B: burning sigil heal.
 *
 * An instant self-buff that applies a `cauterize_sigil` status to the player
 * for 12 seconds. While the sigil is active, any enemy that dies carrying a
 * `burning` DoT heals the player for 5% max HP. Does not break Hide — the
 * sigil is internal, no visible fire.
 *
 * The game layer bridges the `DeathTriggerSystem` to this skill: when
 * `onEnemyDeath` reports a kill where the dying enemy had `burning`, the
 * layer calls `notifyEnemyDeath` with the death context. The skill then
 * checks its own sigil state before healing.
 *
 * Upgrade hooks:
 * - Ash and Salt (`selfDotCleanse`): strip one player DoT per heal
 * - Cremation (`deathBurstDamage`/`deathBurstRadiusTiles`): death-site explosion
 *   that deals damage in a radius and applies burning to survivors
 * - Feeding Flame (`killCooldownRefund`): Flashpoint cooldown refund
 *
 * @see fire-spec.md § Cauterize (`cauterize`)
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Death context (subset of DeathContext relevant to Cauterize)
// ---------------------------------------------------------------------------

export interface CauterizeDeathContext {
  /** Status effect IDs active on the dying enemy at time of death. */
  readonly activeStatuses: string[];
  /** World position of the death (used by Cremation upgrade). */
  readonly deathPosition: { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface CauterizeDeps {
  /**
   * Apply a buff to the player entity.
   * Cauterize uses this to apply the `cauterize_sigil` status.
   */
  readonly applyPlayerStatus: (
    statusId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;
  readonly player: {
    readonly maxHp: number;
    heal(amount: number): void;
  };
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
    getMaxEffect?(skillId: string, key: string): number | undefined;
  };
  /**
   * Returns status IDs of DoT-category effects currently active on the player.
   * Used by Ash and Salt to find a DoT to cleanse on heal.
   */
  readonly getPlayerDotStatuses: () => string[];
  /**
   * Remove a status effect from the player.
   * Used by Ash and Salt to strip one DoT per Cauterize heal proc.
   */
  readonly removePlayerStatus: (statusId: string) => void;
  /**
   * Apply an explosion at a world position. Used by the Cremation upgrade
   * to trigger a death-site burst that damages enemies and applies burning.
   */
  readonly applyExplosion?: (
    position: { x: number; y: number },
    config: {
      damage: number;
      radiusTiles: number;
      statusToApply: string;
      statusDurationMs: number;
      sourceSkillId: string;
    },
  ) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface CauterizeConfig {
  /** Post-activation cooldown in ms. Long tier = 2000ms. */
  readonly cooldownMs: number;
  /** Duration of the cauterize_sigil buff in ms. */
  readonly sigilDurationMs: number;
  /** Fraction of max HP healed per burning-enemy kill (0.05 = 5%). */
  readonly healPercent: number;
}

const DEFAULT_CONFIG: CauterizeConfig = {
  cooldownMs: 2000,        // long cooldown tier
  sigilDurationMs: 12000,  // 12-second sigil
  healPercent: 0.05,       // 5% max HP per proc
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class CauterizeSkillEffect implements SkillEffect {
  readonly skillId = 'cauterize';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: CauterizeDeps;
  private readonly config: CauterizeConfig;

  private _cooldownRemaining = 0;
  private _sigilActive = false;
  private _sigilRemainingMs = 0;
  private _activations = 0;
  private _healsTriggered = 0;
  private _explosionsTriggered = 0;

  constructor(deps: CauterizeDeps, config: Partial<CauterizeConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get sigilActive(): boolean { return this._sigilActive; }
  get sigilRemainingMs(): number { return this._sigilRemainingMs; }
  get healsTriggered(): number { return this._healsTriggered; }
  get explosionsTriggered(): number { return this._explosionsTriggered; }

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  // ---------------------------------------------------------------------------
  // Activation
  // ---------------------------------------------------------------------------

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Apply (or refresh) the cauterize_sigil buff on the player.
    // stackingRule: 'refresh' in status-effects.json resets duration.
    this.deps.applyPlayerStatus('cauterize_sigil', {
      durationMs: this.config.sigilDurationMs,
      sourceSkillId: 'cauterize',
    });

    this._sigilActive = true;
    this._sigilRemainingMs = this.config.sigilDurationMs;
    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {
    // Instant skill — deactivate is a no-op.
  }

  isActive(): boolean {
    return this._sigilActive;
  }

  // ---------------------------------------------------------------------------
  // Per-frame update
  // ---------------------------------------------------------------------------

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (this._sigilActive) {
      this._sigilRemainingMs -= delta;
      if (this._sigilRemainingMs <= 0) {
        this._sigilActive = false;
        this._sigilRemainingMs = 0;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Death trigger notification
  // ---------------------------------------------------------------------------

  /**
   * Called by the game layer when an enemy dies. The skill checks whether:
   * (a) the dying enemy had `burning` active, and (b) the cauterize sigil is
   * currently active on the player. If both conditions are met, the player
   * heals for `healPercent` × max HP.
   *
   * Ash and Salt upgrade: if `selfDotCleanse` is present, each heal also
   * strips the first active DoT-category status from the player.
   */
  notifyEnemyDeath(context: CauterizeDeathContext): void {
    if (!this._sigilActive) return;
    if (!context.activeStatuses.includes('burning')) return;

    const healAmount = this.config.healPercent * this.deps.player.maxHp;
    this.deps.player.heal(healAmount);
    this._healsTriggered++;

    // Ash and Salt: strip one player DoT per heal proc.
    if (this.deps.resolver.hasEffect('cauterize', 'selfDotCleanse')) {
      const dots = this.deps.getPlayerDotStatuses();
      if (dots.length > 0) {
        this.deps.removePlayerStatus(dots[0]);
      }
    }

    // Cremation: death-site explosion when deathBurstDamage is unlocked.
    // The explosion deals damage in a radius and applies burning to survivors,
    // enabling the chain: kill → explosion → burning → more kills → more heals.
    const burstDamage = this.deps.resolver.getMaxEffect?.('cauterize', 'deathBurstDamage');
    if (burstDamage !== undefined && burstDamage > 0) {
      const burstRadius =
        this.deps.resolver.getMaxEffect?.('cauterize', 'deathBurstRadiusTiles') ?? 1;
      this.deps.applyExplosion?.(context.deathPosition, {
        damage: burstDamage,
        radiusTiles: burstRadius,
        statusToApply: 'burning',
        statusDurationMs: 3000,
        sourceSkillId: 'cauterize',
      });
      this._explosionsTriggered++;
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  destroy(): void {
    this._cooldownRemaining = 0;
    this._sigilActive = false;
    this._sigilRemainingMs = 0;
    this._explosionsTriggered = 0;
  }
}
